// Greenhold <SealedDossier> — the unopened artifact.
//
// Epic 8 design pass (2026-08): the visuals are now the parchment
// GATEFOLD with a pressed bordeaux wax seal ("pressed wax · grand oak"),
// ported from the design mirror (design/claude-design/SealedDossier.jsx).
// A-series proportions (height = width × 1.414); the face type + seal
// scale with the rendered width via container-query units so tiny
// dossiers stay overlap-free. The wax seal is the visual tap target.
//
// The exported prop API + the 5-state machine (Story 3.4, UX-DR14) are
// PRESERVED unchanged so every call site keeps compiling; the only
// additive change is the optional `game?` theme note (bottom-left).
//
//   * idle      — parchment gatefold + centred wax seal + portfolio label.
//   * hover     — 4px upward lift + wax-seal red glow. Static via
//                 state="hover" (prototype/SSR) OR dynamic via CSS :hover
//                 when interactive=true. Both paths reach the same visual.
//   * pre-open  — 1.4× scale + wax-seal pulse. Phase 0 of the reveal
//                 ceremony (Story 3.6 owns the trigger; here we stage the
//                 dossier-local effects only).
//   * retired   — grayscale + dim; the bottom-left note reads "retired"
//                 (overrides any `game` note); the seal is non-interactive.
//   * selected  — gold hairline border + gold glow (vault multi-select).
//
// The `dossier?: SealedDossierData` owned-mode prop (Story 3.4) still
// drives the uppercase zero-padded `EDITION 0042` footer; the legacy
// `title` prop remains a preview-mode fallback (`Edition ____`).
//
// Interaction note: in this repo the artifact's click target is owned by
// the PARENT (DossierShopCard wraps the whole card in an <a>; VaultPage
// wraps it in a <button>; RevealCeremony drives the reveal). Making the
// seal itself a <button> would nest interactive elements inside those
// wrappers, so the seal is rendered as a labelled decorative image
// (role="img", aria-label "Break the wax seal") rather than a button —
// honouring the design's "the seal is the tap target" intent without a
// nested-interactive a11y regression.

import type { CSSProperties, FC } from "react";
import { useId } from "react";
import { cn } from "../lib/utils";
import { formatIssueNumber } from "./Certificate";

export type SealedDossierSize = "thumb" | "md" | "lg" | "hero";

export type SealedDossierState =
  | "idle"
  | "hover"
  | "pre-open"
  | "retired"
  | "selected";

/**
 * Owned-mode data for a sealed dossier. Projection of `sealed_dossiers`
 * (id + drop_id + edition_number) joined with the drop's display
 * context (portfolio_title + drop_status + retiring_at). Consumers
 * (Story 4.x vault, Story 5.x marketplace) JOIN these at query time.
 *
 * Story 3.3's `CertificateShare` is a 1:1 mirror of `shares` row;
 * `SealedDossierData` is intentionally a projection because the drop's
 * lifecycle context lives on `drops`, not `sealed_dossiers`.
 */
export interface SealedDossierData {
  id: string;
  drop_id: string;
  edition_number: number;
  portfolio_title: string;
  /** The drop's lifecycle status — when "retired", consumers should
   *  also pass state="retired" to render the retired treatment. Story
   *  3.4 does NOT auto-derive (keeps the state machine explicit). */
  drop_status?: "scheduled" | "live" | "retiring" | "retired";
  /** ISO-8601 when the drop transitions out of `live`; for consumer-
   *  side derivation of the "2 weeks left" warning. The warning is
   *  driven by the existing `showRetirementWarning` prop. */
  retiring_at?: string | null;
  /** Admin-chosen wax-seal base color (`portfolios.seal_color`,
   *  migration 0079), lowercase `#rrggbb`. Absent/null = the default
   *  bordeaux. The full shade ramp derives via `deriveWaxPalette`. */
  seal_color?: string | null;
}

export interface SealedDossierProps {
  /**
   * Legacy preview-mode label (Story 2.7 DossierShopCard consumer).
   * Optional now (was required pre-Story-3.4). When `dossier` is
   * provided, `dossier.portfolio_title` takes precedence + the
   * edition footer renders `EDITION ####` instead of the legacy
   * `Edition ____` placeholder.
   *
   * @deprecated Use `dossier` for owned-mode data. `title` remains
   * supported for backward compatibility with DossierShopCard's
   * preview mode (no edition number known until purchase).
   */
  title?: string;
  /**
   * Optional retirement-warning badge in the upper-right. Set true by
   * callers when the drop status === "retiring" (last 2 weeks of shop
   * life). Story 3.4 does NOT auto-derive from `dossier.retiring_at`
   * — consumers derive (DossierShopCard L118 does `drop.status ===
   * "retiring"`).
   */
  showRetirementWarning?: boolean;
  /**
   * Visual size variant. `md` is the shop card size; `thumb` is for the
   * vault grid (Epic 4); `lg` is the dossier-detail page (Story 2.8);
   * `hero` is the reveal-ceremony stage (Story 3.6). Caps the face
   * width; the face keeps A-series (width × 1.414) proportions and the
   * type scales with the rendered width.
   */
  size?: SealedDossierSize;
  /**
   * When true, the artifact lifts 4px on hover + the wax seal gains a
   * red glow + cursor becomes pointer. Used when the artifact is itself
   * the clickable link target (rare — the shop-card consumer wraps the
   * whole card in an <a> instead, so the default `false` is correct for
   * shop cards).
   */
  interactive?: boolean;
  /**
   * Optional className for outer container — supports composition
   * (e.g., aspect-ratio override in special layouts).
   */
  className?: string;
  /**
   * Owned-mode data carrying edition number + portfolio context.
   * When provided, the edition footer renders the uppercase zero-
   * padded `EDITION 0042`. Required for `state="retired"` so the
   * treatment reads the portfolio context.
   */
  dossier?: SealedDossierData;
  /**
   * Optional game/theme note, printed bottom-left ("Counter-Strike",
   * "Valorant"). Omitted = nothing shown; the `retired` state overrides
   * it with "retired". Additive in the Epic 8 design pass.
   */
  game?: string;
  /**
   * Visual state machine variant. Default `idle`. `hover` forces the
   * hover look (lift + seal red-glow) for prototype/programmatic
   * display; the CSS `:hover` path on `interactive=true` drives the
   * same look on mouse hover. `pre-open` stages Phase 0 of the ceremony
   * (Story 3.6 owns the trigger). The `state` prop is orthogonal with
   * `interactive` — a vault-grid multi-select hover-over renders both.
   */
  state?: SealedDossierState;
  /**
   * Preview-mode wax-seal base color for callers that render via the
   * legacy `title` prop (shop cards; no owned `dossier` object).
   * `dossier.seal_color` wins when both are present. Absent/null =
   * default bordeaux.
   */
  sealColor?: string | null;
}

// ── Face-width cap per size (px). The face fills its column up to this
//    cap, then holds A-series proportions; type + seal scale with the
//    rendered width (container-query units below). ────────────────────
const SIZE_MAX_WIDTH: Record<SealedDossierSize, number> = {
  thumb: 96,
  md: 240,
  lg: 360,
  hero: 480,
};

// ── Procedural seal geometry (ported from __sdWobble in the design
//    mirror). Deterministic per seed, so the two rings are computed once
//    at module load rather than per render. ───────────────────────────
function wobblePath(
  cx: number,
  cy: number,
  rBase: number,
  variance: number,
  n: number,
  seed: number,
): string {
  let rnd = seed;
  const rand = () => (rnd = (rnd * 9301 + 49297) % 233280) / 233280;
  const pts: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const r = rBase + (rand() - 0.5) * 2 * variance;
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  let d = "M" + pts[0][0].toFixed(1) + " " + pts[0][1].toFixed(1);
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d +=
      "C" +
      c1[0].toFixed(1) + " " + c1[1].toFixed(1) + " " +
      c2[0].toFixed(1) + " " + c2[1].toFixed(1) + " " +
      p2[0].toFixed(1) + " " + p2[1].toFixed(1);
  }
  return d + "Z";
}

const WAX_PATH = wobblePath(100, 100, 84, 8, 14, 11);
const PLATEAU_PATH = wobblePath(100, 100, 63, 2.5, 12, 5);

// ── Seal color derivation (admin-configurable seals, migration 0079) ──
// Admins author ONE base hex per portfolio (`portfolios.seal_color`);
// the whole pressed-wax ramp — highlight, body, shadow, plateau, rim
// ink, monogram, cast shadows, hover glow — derives from it here. The
// authored bordeaux hexes below are the design-mirror originals; every
// other base replays each shade's HSL offset from `WAX_DEFAULT_COLOR`
// onto the new base, so `deriveWaxPalette(WAX_DEFAULT_COLOR)` returns
// the authored hexes byte-for-byte and a green/blue/black seal keeps
// the exact same depth relationships as the bordeaux one.

export const WAX_DEFAULT_COLOR = "#8c2430";

const WAX_ROLE_HEXES = {
  /** waxG 0% — the lit top-left of the wax body. */
  wax400: "#a83a42",
  /** waxG 55% — the wax body (== WAX_DEFAULT_COLOR). */
  wax500: "#8c2430",
  /** waxG 100% — the shadowed rim of the wax body. */
  wax700: "#6e1a24",
  /** platG 0% — the pressed plateau, lit. */
  plat400: "#8c232e",
  /** platG 100% — the pressed plateau, shadowed. */
  plat600: "#7e1f2a",
  /** Rim rings + oak crest + branch strokes. */
  ink: "#5f141d",
  /** The GH monogram. */
  deep: "#4a0d15",
  /** shG stops — cast shadow along the lower-left edge (rgba(40,5,9,…)). */
  shadowEdge: "#280509",
  /** platShG stops — plateau inner shadow (rgba(30,3,7,…)). */
  shadowPlat: "#1e0307",
  /** Hover/tap glow (rgba(178,45,38,0.6) in the pre-0079 art). */
  glow: "#b22d26",
  /** RevealCeremony's tear-crack ink (its pre-0079 WAX_TEAR_INK). */
  tearInk: "#54121b",
} as const;

type WaxRole = keyof typeof WAX_ROLE_HEXES;
export type WaxPalette = Record<WaxRole, string>;

const HEX_RE = /^#[0-9a-f]{6}$/;

function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const c = max - min;
  if (c === 0) return [0, 0, l];
  const s = c / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / c + 6) % 6;
  else if (max === g) h = (b - r) / c + 2;
  else h = (r - g) / c + 4;
  return [h * 60, s, l];
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const [r1, g1, b1] =
    hp < 1 ? [c, x, 0]
    : hp < 2 ? [x, c, 0]
    : hp < 3 ? [0, c, x]
    : hp < 4 ? [0, x, c]
    : hp < 5 ? [x, 0, c]
    : [c, 0, x];
  const m = l - c / 2;
  const to255 = (v: number) =>
    Math.round(Math.min(1, Math.max(0, v + m)) * 255)
      .toString(16)
      .padStart(2, "0");
  return "#" + to255(r1) + to255(g1) + to255(b1);
}

const WAX_BASE_HSL = hexToHsl(WAX_DEFAULT_COLOR);
// Per-role [dH, dS, dL] offsets from the bordeaux base, computed once.
const WAX_ROLE_OFFSETS = Object.fromEntries(
  (Object.entries(WAX_ROLE_HEXES) as [WaxRole, string][]).map(([role, hex]) => {
    const [h, s, l] = hexToHsl(hex);
    return [
      role,
      [h - WAX_BASE_HSL[0], s - WAX_BASE_HSL[1], l - WAX_BASE_HSL[2]],
    ];
  }),
) as Record<WaxRole, [number, number, number]>;

const paletteCache = new Map<string, WaxPalette>();

/**
 * Derive the full pressed-wax palette from one base hex. Falls back to
 * the authored bordeaux for a missing or malformed base (the backend
 * CHECK-constrains the column to lowercase `#rrggbb`, so malformed only
 * happens with hand-fed props). Case-insensitive; cached per base.
 */
export function deriveWaxPalette(base?: string | null): WaxPalette {
  const normalized = base?.toLowerCase() ?? WAX_DEFAULT_COLOR;
  if (!HEX_RE.test(normalized)) return WAX_ROLE_HEXES;
  if (normalized === WAX_DEFAULT_COLOR) return WAX_ROLE_HEXES;
  const cached = paletteCache.get(normalized);
  if (cached) return cached;
  const [h, s, l] = hexToHsl(normalized);
  const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
  const palette = Object.fromEntries(
    (Object.entries(WAX_ROLE_OFFSETS) as [WaxRole, [number, number, number]][]).map(
      ([role, [dh, ds, dl]]) => [
        role,
        hslToHex(h + dh, clamp01(s + ds), clamp01(l + dl)),
      ],
    ),
  ) as WaxPalette;
  paletteCache.set(normalized, palette);
  return palette;
}

/** `#rrggbb` + alpha → `rgba(r,g,b,a)` for the gradient/glow stops. */
function rgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const OAK_PATH =
  "M50 10 C41 8 33 13 30 20 C21 19 14 26 15 34 C8 36 5 46 11 52 C8 59 14 67 22 66 C25 73 35 76 41 71 C43 69 44 68 45 66 C46 71 46 76 45 80 C43 86 41 89 38 92 L62 92 C59 89 57 86 55 80 C54 76 54 71 55 66 C56 68 57 69 59 71 C65 76 75 73 78 66 C86 67 92 59 89 52 C95 46 92 36 85 34 C86 26 79 19 70 20 C67 13 59 8 50 10 Z";
const OAK_BRANCHES =
  "M30 92 C36 90 44 89 50 89 C56 89 64 90 70 92 Z M45 66 C42 61 38 58 33 56 M55 66 C58 61 62 58 67 56";

/**
 * The pressed-wax seal SVG art. Unique gradient/filter ids per mount
 * (via `uid`) so multiple seals on one page don't collide. Decorative —
 * the wrapping element carries the aria-label.
 *
 * `shadow` (default true) draws the cast drop-shadow. Pass false when the
 * seal is layered over another copy of itself (e.g. the reveal ceremony's
 * "intact sealer" over the two torn halves) so the shadow isn't cast twice.
 *
 * `color` is the portfolio's wax-seal base hex (migration 0079);
 * omitted/null renders the default bordeaux. The full ramp derives via
 * `deriveWaxPalette` so any base keeps the pressed-wax depth.
 */
export function WaxSealArt({
  uid,
  shadow = true,
  color,
}: {
  uid: string;
  shadow?: boolean;
  color?: string | null;
}) {
  const id = (n: string) => uid + n;
  const u = (n: string) => `url(#${id(n)})`;
  const wax = deriveWaxPalette(color);
  return (
    <svg
      viewBox="0 0 200 200"
      style={{
        position: "absolute",
        inset: "-13%",
        width: "126%",
        height: "126%",
        filter: shadow ? "drop-shadow(0 4px 6px rgba(31,20,8,0.4))" : "none",
        pointerEvents: "none",
      }}
    >
      <defs>
        <radialGradient id={id("waxG")} cx="40%" cy="32%" r="75%">
          <stop offset="0%" stopColor={wax.wax400} />
          <stop offset="55%" stopColor={wax.wax500} />
          <stop offset="100%" stopColor={wax.wax700} />
        </radialGradient>
        <radialGradient id={id("platG")} cx="45%" cy="40%" r="75%">
          <stop offset="0%" stopColor={wax.plat400} />
          <stop offset="100%" stopColor={wax.plat600} />
        </radialGradient>
        <radialGradient id={id("sheenG")} cx="35%" cy="25%" r="60%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.2)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
        <linearGradient id={id("hiG")} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
          <stop offset="45%" stopColor="rgba(255,255,255,0.12)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
        <linearGradient id={id("shG")} x1="0" y1="1" x2="0.3" y2="0">
          <stop offset="0%" stopColor={rgba(wax.shadowEdge, 0.55)} />
          <stop offset="60%" stopColor={rgba(wax.shadowEdge, 0)} />
        </linearGradient>
        <linearGradient id={id("platShG")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={rgba(wax.shadowPlat, 0.5)} />
          <stop offset="45%" stopColor={rgba(wax.shadowPlat, 0)} />
          <stop offset="85%" stopColor="rgba(255,255,255,0.14)" />
        </linearGradient>
        <filter id={id("soft1")}>
          <feGaussianBlur stdDeviation="2" />
        </filter>
        <filter id={id("soft2")}>
          <feGaussianBlur stdDeviation="1.1" />
        </filter>
      </defs>
      <path d={WAX_PATH} fill={u("waxG")} />
      <path
        d={WAX_PATH}
        fill="none"
        stroke={u("hiG")}
        strokeWidth="4.5"
        filter={u("soft1")}
        transform="translate(1.8 2.2) scale(0.985)"
      />
      <path
        d={WAX_PATH}
        fill="none"
        stroke={u("shG")}
        strokeWidth="5"
        filter={u("soft1")}
        transform="translate(3 3) scale(0.97)"
      />
      <path d={PLATEAU_PATH} fill={u("platG")} />
      <path
        d={PLATEAU_PATH}
        fill="none"
        stroke={u("platShG")}
        strokeWidth="4"
        filter={u("soft2")}
        transform="translate(2 2) scale(0.98)"
      />
      <g style={{ filter: "drop-shadow(0 1px 0 rgba(255,255,255,0.25))" }}>
        <circle
          cx="100"
          cy="100"
          r="56"
          fill="none"
          stroke={wax.ink}
          strokeWidth="2.4"
          strokeDasharray="0.1 6.1"
          strokeLinecap="round"
          strokeOpacity="0.8"
        />
        <circle
          cx="100"
          cy="100"
          r="50"
          fill="none"
          stroke={wax.ink}
          strokeWidth="1.6"
          strokeDasharray="0.1 4.9"
          strokeLinecap="round"
          strokeOpacity="0.7"
          transform="rotate(14 100 100)"
        />
        <g transform="translate(59 55) scale(0.82)">
          <path d={OAK_PATH} fill={wax.ink} fillOpacity="0.78" />
          <path
            d={OAK_BRANCHES}
            fill="none"
            stroke={wax.ink}
            strokeWidth="2"
            strokeLinecap="round"
            strokeOpacity="0.8"
          />
        </g>
        <text
          x="100"
          y="92"
          textAnchor="middle"
          fontFamily="Georgia, 'Times New Roman', serif"
          fontSize="17"
          letterSpacing="1"
          fill={wax.deep}
          fillOpacity="0.92"
        >
          GH
        </text>
      </g>
      <ellipse cx="72" cy="62" rx="52" ry="40" fill={u("sheenG")} />
    </svg>
  );
}

/**
 * Sealed-dossier artifact — the unopened artifact users buy from the
 * shop (Story 2.10), receive as a welcome grant (Story 3.9), or acquire
 * from the sealed-dossier marketplace (Story 5.x). Parchment gatefold +
 * pressed wax seal (Epic 8 design pass); full 5-state machine per
 * UX-DR14.
 */
export const SealedDossier: FC<SealedDossierProps> = ({
  title,
  showRetirementWarning = false,
  size = "md",
  interactive = false,
  className,
  dossier,
  game,
  state = "idle",
  sealColor,
}) => {
  const uid = "ghsd-" + useId().replace(/:/g, "") + "-";

  // Owned-mode color wins over the preview-mode prop; the palette also
  // feeds the hover glow (via the --gh-seal-glow custom property below).
  const effectiveSealColor = dossier?.seal_color ?? sealColor ?? null;
  const sealGlow = rgba(deriveWaxPalette(effectiveSealColor).glow, 0.6);

  // Derived display values — dossier wins when present; legacy `title`
  // is the fallback so DossierShopCard's existing call site keeps
  // rendering exactly as today.
  const effectiveTitle = dossier?.portfolio_title ?? title ?? "";
  // "ISSUE" is the consumer-facing label for edition_number (2026-08 copy
  // rename; the API field + code identifiers keep the edition_ vocabulary).
  const editionText = dossier
    ? `ISSUE ${formatIssueNumber(dossier.edition_number)}`
    : "Issue ____";

  const isHoverForced = state === "hover";
  const isPreOpen = state === "pre-open";
  const isRetired = state === "retired";
  const isSelected = state === "selected";
  // "Live" seal — reads as tappable (glow + red hover). Retired seals
  // are inert. Mirrors the design's `open` gate.
  const live = !isRetired;

  // Multi-segment aria-label builder on the outer article. Base +
  // per-state suffixes — robust across all AT.
  const ariaSegments = [`Sealed dossier: ${effectiveTitle}`];
  if (dossier) ariaSegments.push(`edition ${dossier.edition_number}`);
  if (isRetired) ariaSegments.push("retired");
  if (isSelected) ariaSegments.push("selected");
  const ariaLabel = ariaSegments.join(", ");

  const noteText = isRetired ? "retired" : game || "";

  // Elevation: selected → gold glow; a live interactive artifact → soft
  // gold glow; otherwise the plain lifted shadow. (Mirrors the design's
  // shadow ladder.)
  const boxShadow = isSelected
    ? "var(--gh-shadow-lg, 0 8px 16px rgba(14,42,31,0.14)), var(--gh-glow-md, 0 0 16px rgba(195,146,53,0.5))"
    : interactive && live
      ? "var(--gh-shadow-lg, 0 8px 16px rgba(14,42,31,0.14)), var(--gh-glow-sm, 0 0 8px rgba(195,146,53,0.4))"
      : "var(--gh-shadow-lg, 0 8px 16px rgba(14,42,31,0.14))";

  const containerStyle: CSSProperties = {
    position: "relative",
    // Establish a query container so the face type + seal scale with the
    // rendered width (cqw units below) — "tiny dossiers stay overlap-free".
    containerType: "inline-size",
    width: "100%",
    maxWidth: SIZE_MAX_WIDTH[size],
    aspectRatio: "1 / 1.414",
    borderRadius: 6,
    border: isSelected
      ? "1px solid var(--gh-gold-500, #c39235)"
      : "1px solid rgba(195,146,53,0.45)",
    background: "var(--gh-parchment, #e7dec4)",
    boxShadow,
    filter: isRetired ? "grayscale(0.55) brightness(0.96)" : "none",
    opacity: isRetired ? 0.8 : 1,
    isolation: "isolate",
  };

  const mono = "var(--gh-font-mono, 'JetBrains Mono', ui-monospace, monospace)";

  return (
    <div
      role="article"
      aria-label={ariaLabel}
      tabIndex={interactive ? 0 : undefined}
      style={containerStyle}
      className={cn(
        "overflow-hidden",
        // Static-state hover lift (state="hover" — prototype/SSR).
        isHoverForced &&
          "-translate-y-1 transition-transform duration-200 motion-reduce:transform-none",
        // CSS-driven hover lift for the interactive path — both reach the
        // same visual; cn() merges them.
        interactive &&
          "cursor-pointer transition-transform duration-200 hover:-translate-y-1 motion-reduce:transform-none motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500 focus-visible:ring-offset-2",
        // Pre-open ceremony staging — 1.4× scale. Reduced-motion gated.
        isPreOpen &&
          "scale-[1.4] transition-transform duration-300 motion-reduce:scale-100 motion-reduce:transition-none",
        className,
      )}
    >
      {/* Left flap of the gatefold — carries the shadowed seam. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: "51%",
          zIndex: 2,
          background:
            "linear-gradient(90deg, var(--gh-parchment-deep, #d6c9a4) 0, var(--gh-parchment-hi, #ece4cc) 20%, var(--gh-parchment, #e7dec4) 80%, var(--gh-parchment-lo, #ddd0ad) 100%)",
          boxShadow: "3px 0 4px rgba(58,47,28,0.28)",
        }}
      />
      {/* Right flap. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          width: "50%",
          zIndex: 1,
          background:
            "linear-gradient(90deg, #e0d5b6 0, #e9e0c8 35%, #e4dabd 85%, #d0c39c 100%)",
        }}
      />
      {/* Aged-paper wear + inner vignette. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 3,
          pointerEvents: "none",
          borderRadius: 6,
          background:
            "radial-gradient(ellipse at 18% 12%, rgba(122,90,29,0.10), transparent 40%), radial-gradient(ellipse at 85% 78%, rgba(122,90,29,0.12), transparent 45%), radial-gradient(ellipse at 60% 30%, rgba(122,90,29,0.05), transparent 50%)",
          boxShadow: "inset 0 0 24px rgba(58,47,28,0.22)",
        }}
      />
      {/* Portfolio label — the ONLY identity shown while sealed. Constrained
          to the left flap so it NEVER crosses the gatefold seam: the box ends
          at left(9%) + maxWidth(40%) = 49%, inside the 51% flap; long words
          break rather than overflow, and the text clamps to 3 lines (which
          bottoms out above the centred wax seal) with an ellipsis.
          Long titles shrink a step BEFORE they clamp: the flap fits ~12 mono
          characters per line at the base 4.6cqw, so anything past ~26
          characters would spend its third line on an ellipsis. The smaller
          steps keep the seam and the three-line geometry intact while
          letting real titles (em-dashes, volume numbers) print in full. */}
      <div
        style={{
          position: "absolute",
          top: "9cqw",
          left: "9cqw",
          maxWidth: "40%",
          zIndex: 4,
          fontFamily: mono,
          fontSize:
            effectiveTitle.length > 36
              ? "3.5cqw"
              : effectiveTitle.length > 26
                ? "4.0cqw"
                : "4.6cqw",
          letterSpacing: "0.14em",
          lineHeight: 1.6,
          textTransform: "uppercase",
          color: "var(--gh-parchment-ink, #3a2f1c)",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 3,
          overflow: "hidden",
          overflowWrap: "break-word",
          wordBreak: "break-word",
          hyphens: "auto",
        }}
      >
        {effectiveTitle}
      </div>
      {/* Bottom-left note — game/theme, or "retired". */}
      {noteText ? (
        <div
          style={{
            position: "absolute",
            bottom: "4.5cqw",
            left: "4.5cqw",
            maxWidth: "46%",
            zIndex: 4,
            fontFamily: mono,
            fontSize: "4.6cqw",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "rgba(58,47,28,0.55)",
          }}
        >
          {noteText}
        </div>
      ) : null}
      {/* Bottom-right edition. */}
      <div
        style={{
          position: "absolute",
          bottom: "4.5cqw",
          right: "4.5cqw",
          zIndex: 4,
          fontFamily: mono,
          fontSize: "4.6cqw",
          color: "rgba(58,47,28,0.7)",
        }}
      >
        {editionText}
      </div>

      {/* Retirement-warning badge, upper-right. Preserved from v1 — set
          by callers when the drop is in its last two weeks. */}
      {showRetirementWarning && (
        <div
          role="status"
          style={{
            position: "absolute",
            top: "4.5cqw",
            right: "4.5cqw",
            zIndex: 4,
            fontFamily: mono,
            fontSize: "3.6cqw",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 600,
            padding: "1.5cqw 2.5cqw",
            borderRadius: 3,
            background: "var(--gh-wax-500, #8c2430)",
            color: "#faefef",
          }}
        >
          2 weeks left
        </div>
      )}

      {/* The pressed wax seal — the visual tap target. Labelled decorative
          image (see interaction note in the file header); the parent owns
          the actual click. Red glow on hover; pulse during pre-open. */}
      <span
        role="img"
        aria-label="Break the wax seal"
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%,-50%)",
          width: "34cqw",
          height: "34cqw",
          zIndex: 5,
          cursor: interactive && live ? "pointer" : "default",
          // Glow tint follows the seal color; consumed by the static +
          // :hover drop-shadow classes below.
          ["--gh-seal-glow" as string]: sealGlow,
        }}
        className={cn(
          "block",
          isHoverForced &&
            live &&
            "drop-shadow-[0_0_8px_var(--gh-seal-glow)]",
          interactive &&
            live &&
            "transition-[filter] duration-200 hover:drop-shadow-[0_0_8px_var(--gh-seal-glow)]",
          isPreOpen &&
            "animate-sealed-dossier-seal-pulse motion-reduce:animate-none",
        )}
      >
        <WaxSealArt uid={uid} color={effectiveSealColor} />
      </span>
    </div>
  );
};
